import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, AlertTriangle, CalendarOff } from "lucide-react";
import { Input } from "../ui/input";
import { fmtDay } from "./AdminOffers";
import AdminDateField, { londonToday } from "./AdminDateField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "rounded-lg border-white/10 bg-[#0b0c0f] text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0 [color-scheme:dark]";
const L = ({ children }) => <label className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-white/60">{children}</label>;
const today = londonToday;
const REASONS = ["Holiday", "Closed", "Bank holiday", "Fully booked"];

const BookingsWarning = ({ bookings }) => (
  <div className="mt-2 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-sm text-amber-200" data-testid="closure-bookings-warning">
    <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" />{bookings.length} booking{bookings.length > 1 ? "s" : ""} on these dates. They're kept, decline them in Bookings if needed.</div>
    <ul className="mt-1.5 space-y-0.5 text-amber-100/80">
      {bookings.map((b) => <li key={b.id}>{b.reference} · {b.name} · {fmtDay(b.preferred_date)} {b.preferred_time} · {b.booking_status}</li>)}
    </ul>
  </div>
);

const AdminClosures = ({ token }) => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ start_date: today(), end_date: today(), reason: "Holiday" });
  const [saving, setSaving] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => axios.get(`${API}/admin/closures`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setItems(data)).catch(() => toast.error("Couldn't load closed days")), [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/closures`, f, auth);
      if (data.bookings.length) toast.warning(`Closed, but ${data.bookings.length} existing booking(s) fall on these dates`);
      else toast.success("Dates closed · customers can't book them now");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check the dates.");
    } finally { setSaving(false); }
  };
  const remove = async (c) => {
    try { await axios.delete(`${API}/admin/closures/${c.id}`, auth); toast.success("Dates reopened for booking"); load(); }
    catch { toast.error("Couldn't reopen dates"); }
  };

  return (
    <div data-testid="admin-closures">
      <h1 className="flex items-center gap-3 font-display text-4xl font-bold text-white"><CalendarOff className="h-8 w-8 text-brand" />Closed days</h1>
      <p className="mt-1 text-white/55">Close single days or holidays. Closed dates are greyed out in the booking calendar.</p>
      <form onSubmit={submit} className="mt-8 grid gap-4 rounded-2xl border border-white/10 bg-[#111217] p-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="closure-form">
        <div><L>From</L><AdminDateField value={f.start_date} min={today()} onChange={(v) => setF({ ...f, start_date: v, end_date: v > f.end_date ? v : f.end_date })} testid="closure-start" /></div>
        <div><L>To</L><AdminDateField value={f.end_date} min={f.start_date} onChange={(v) => setF({ ...f, end_date: v })} testid="closure-end" /></div>
        <div>
          <L>Reason (shown to customers)</L>
          <Input required maxLength={60} list="closure-reasons" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={`${field} h-11`} data-testid="closure-reason" />
          <datalist id="closure-reasons">{REASONS.map((r) => <option key={r} value={r} />)}</datalist>
        </div>
        <div className="flex items-end">
          <button disabled={saving} className="btn-red flex h-11 w-full items-center justify-center gap-2 rounded-lg px-5 text-sm font-semibold disabled:opacity-50" data-testid="closure-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Close dates</button>
        </div>
      </form>
      <div className="mt-6 space-y-3" data-testid="closures-list">
        {items.length === 0 && <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-white/45">No closed days. You're open as normal.</div>}
        {items.map((c) => (
          <div key={c.id} className="rounded-xl border border-white/10 bg-[#111217] px-5 py-4" data-testid={`closure-row-${c.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-md bg-brand/15 px-2 py-0.5 text-[11px] font-bold uppercase text-brand">{c.reason}</span>
              <span className="font-semibold text-white">{c.start_date === c.end_date ? fmtDay(c.start_date) : `${fmtDay(c.start_date)} – ${fmtDay(c.end_date)}`}</span>
              <button onClick={() => remove(c)} className="ml-auto flex items-center gap-1 text-sm text-white/50 hover:text-brand" data-testid={`closure-delete-${c.id}`}><Trash2 className="h-4 w-4" />Reopen</button>
            </div>
            {c.bookings.length > 0 && <BookingsWarning bookings={c.bookings} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminClosures;
